/**
 * Global Search Bar with typeahead dropdown.
 * Searches Jobs, POs, Parts, and Vendors all in one place.
 * Replaces the basic search input in TopNav.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchPurchaseOrders } from '../../api/purchasing';

// Status badge colors for POs
const PO_STATUS_COLORS = {
  po_uploaded: 'bg-blue-100 text-blue-700',
  po_sent: 'bg-sky-100 text-sky-700',
  confirmed: 'bg-teal-100 text-teal-700',
  partial_confirmed: 'bg-cyan-100 text-cyan-700',
  partial_received: 'bg-amber-100 text-amber-700',
  draft: 'bg-gray-100 text-gray-600',
};

const PO_STATUS_LABELS = {
  draft: 'Draft',
  po_uploaded: 'Ordered',
  po_sent: 'Sent',
  confirmed: 'Confirmed',
  partial_confirmed: 'Partial',
  partial_received: 'Partial Recv',
};

export default function GlobalSearchBar({ className = '' }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ jobs: [], pos: [] });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Flatten results for keyboard navigation
  const allResults = [
    ...results.pos.map((po) => ({ type: 'po', data: po })),
    ...results.jobs.map((job) => ({ type: 'job', data: job })),
  ];

  // Search function
  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setResults({ jobs: [], pos: [] });
      setOpen(false);
      return;
    }

    setLoading(true);
    try {
      // Search POs
      const poResults = await searchPurchaseOrders(q);

      // Simple local job search - match against query in job number
      // (In a full implementation, this would call a jobs search API)
      const jobResults = [];

      setResults({
        jobs: jobResults,
        pos: poResults || [],
      });

      const hasResults = (poResults?.length || 0) + jobResults.length > 0;
      setOpen(hasResults || q.length >= 2);
      setSelectedIndex(-1);
    } catch (err) {
      console.error('Search error:', err);
      setResults({ jobs: [], pos: [] });
      setOpen(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      doSearch(query);
    }, 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, doSearch]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle selection
  function handleSelect(item) {
    if (item.type === 'po') {
      const po = item.data;
      if (po.job_id) {
        navigate(`/jobs/${po.job_id}?tab=orders`);
      }
    } else if (item.type === 'job') {
      navigate(`/jobs/${item.data.id}`);
    }
    setQuery('');
    setOpen(false);
  }

  // Keyboard navigation
  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      // If we have a selected item, use it
      if (selectedIndex >= 0 && allResults[selectedIndex]) {
        handleSelect(allResults[selectedIndex]);
      } else if (allResults.length > 0) {
        // Otherwise select first result
        handleSelect(allResults[0]);
      }
      return;
    }

    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }

    if (!open || allResults.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, allResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    }
  }

  const totalResults = allResults.length;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.length >= 2 && totalResults > 0 && setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search PO#, jobs..."
          className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        {/* Search icon */}
        <svg
          className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
        {/* Loading spinner */}
        {loading && (
          <svg
            className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 animate-spin"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        )}
      </div>

      {/* Dropdown results */}
      {open && (
        <div className="absolute z-50 mt-1 w-80 bg-white rounded-lg shadow-lg border border-gray-200 max-h-96 overflow-y-auto">
          {totalResults === 0 && !loading ? (
            <div className="px-3 py-3 text-sm text-gray-500 text-center">
              No results found for "{query}"
            </div>
          ) : (
            <>
              {/* PO Results */}
              {results.pos.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider bg-gray-50 border-b border-gray-100">
                    Purchase Orders
                  </div>
                  <ul>
                    {results.pos.map((po, idx) => {
                      const globalIdx = idx;
                      return (
                        <li
                          key={po.id}
                          onClick={() => handleSelect({ type: 'po', data: po })}
                          className={`px-3 py-2 cursor-pointer border-b border-gray-50 last:border-0 ${
                            globalIdx === selectedIndex ? 'bg-blue-50' : 'hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              {/* PO Number */}
                              <span className="font-mono text-sm font-semibold text-gray-900">
                                {po.po_number || 'No PO#'}
                              </span>
                              {/* Status badge */}
                              <span
                                className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                                  PO_STATUS_COLORS[po.order_status] || 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                {PO_STATUS_LABELS[po.order_status] || po.order_status}
                              </span>
                            </div>
                            {/* Job number */}
                            {po.job_number && (
                              <span className="text-xs font-medium text-blue-600">
                                Job #{po.job_number}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
                            <span className="font-medium text-gray-600">{po.vendor_name}</span>
                            {po.customer_name && (
                              <>
                                <span className="text-gray-300">•</span>
                                <span>{po.customer_name}</span>
                              </>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* Job Results (if we add job search later) */}
              {results.jobs.length > 0 && (
                <div>
                  <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider bg-gray-50 border-b border-gray-100">
                    Jobs
                  </div>
                  <ul>
                    {results.jobs.map((job, idx) => {
                      const globalIdx = results.pos.length + idx;
                      return (
                        <li
                          key={job.id}
                          onClick={() => handleSelect({ type: 'job', data: job })}
                          className={`px-3 py-2 cursor-pointer border-b border-gray-50 last:border-0 ${
                            globalIdx === selectedIndex ? 'bg-blue-50' : 'hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm font-semibold text-gray-900">
                              #{job.job_number}
                            </span>
                            <span className="text-xs text-gray-500">{job.customer_name}</span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* Hint */}
              {totalResults > 0 && (
                <div className="px-3 py-1.5 text-[10px] text-gray-400 bg-gray-50 border-t border-gray-100">
                  ↑↓ to navigate • Enter to select • Esc to close
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
